import { z } from 'zod';

// Simple UUID v4-ish validation (accepts any RFC4122 UUID)
export const isUuid = (value: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
};

// Username validation
export const usernameSchema = z.string()
  .trim()
  .min(2, 'Username must be at least 2 characters')
  .max(20, 'Username must be less than 20 characters')
  .regex(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores')
  .regex(/^[a-zA-Z]/, 'Username must start with a letter');

// Authentication validation
export const authSchema = z.object({
  email: z.string().email('Invalid email format').max(255, 'Email too long'),
  password: z.string()
    .min(6, 'Password must be at least 6 characters')
    .max(128, 'Password too long'),
  name: z.string().trim().min(1, 'Name required').max(100, 'Name too long').optional()
});

// Listing validation
export const listingSchema = z.object({
  title: z.string().trim().min(3, 'Title must be at least 3 characters').max(100, 'Title too long'),
  price: z.string().refine((val) => {
    const num = parseFloat(val);
    return !isNaN(num) && num > 0 && num <= 10000000;
  }, 'Price must be between $0 and $10,000,000'),
  description: z.string().trim().min(10, 'Description must be at least 10 characters').max(2000, 'Description too long'),
  location: z.string().trim().min(1, 'Location required').max(200, 'Location too long'),
  category: z.enum(['goods', 'services']),
  subcategory: z.string().min(1, 'Subcategory required')
});

// Forum post validation
export const forumPostSchema = z.object({
  title: z.string().trim().max(200, 'Title too long').optional(),
  body: z.string().trim().min(10, 'Post must be at least 10 characters').max(5000, 'Post too long'),
  area: z.string().trim().min(2, 'Area required').max(100, 'Area too long').optional(),
  county: z.string().trim().min(1, 'County required').max(100, 'County too long'),
  city: z.string().trim().max(100, 'City too long').optional(),
  township: z.string().trim().max(100, 'Township too long').optional(),
  hashtags: z.array(z.string().trim().min(1).max(50)).max(10, 'Maximum 10 hashtags allowed').optional(),
  topic: z.enum(['Local News', 'Wanna Join?', 'Hood Q&A', 'Local Eats', 'Lost & Found', 'Local Info'])
});

// Waitlist validation
export const waitlistSchema = z.object({
  email: z.string().email('Invalid email format').max(255, 'Email too long')
});

// Search validation
export const searchSchema = z.object({
  query: z.string().trim().min(1, 'Search query required').max(200, 'Search query too long')
});
